import { bind, state } from "@react-rxjs/core"
import { createSignal } from "@react-rxjs/utils"
import { scan } from "rxjs"

export type EquitiesServiceName =
  | "login"
  | "watchlist"
  | "instruments"
  | "orders"
  | "positions"
  | "marketData"
  | "marketStatus"
  | "account"

type EquitiesErrorRecord = Partial<Record<EquitiesServiceName, string>>

/**
 * `EquitiesErrorEvent` is an event shape (not a snapshot record) so publishers
 * only know their own source; explicit null clears let consumers expose the
 * recovery transition rather than forcing consumers to infer it from absence.
 * `message: null` -> clear the error for the given service
 * `message: string` -> set the error for the given service
 */
type EquitiesErrorEvent = {
  source: EquitiesServiceName
  message: string | null
}

const [equitiesErrorEvent$, setEquitiesErrorEvent] =
  createSignal<EquitiesErrorEvent>()

const equitiesError$ = state(
  equitiesErrorEvent$.pipe(
    scan<EquitiesErrorEvent, EquitiesErrorRecord>(
      (acc, { source, message }) => {
        if (message) {
          return { ...acc, [source]: message }
        }
        const { [source]: _, ...rest } = acc
        return rest
      },
      {} as EquitiesErrorRecord,
    ),
  ),
)

/**
 * Publishes an error for the given source. One active error per source at a time —
 * republishing overwrites the value in the same key. Combine concurrent conditions
 * from the service layer into a single message before calling.
 */
export const publishEquitiesError = (event: EquitiesErrorEvent) => {
  setEquitiesErrorEvent(event)
}

/**
 * Recovery signal: removes the source's entry from the record and advances the
 * queue. Call from the service's success path.
 */
export const clearEquitiesError = (source: EquitiesServiceName) => {
  publishEquitiesError({ source, message: null })
}

/**
 * Current equities error record, ordered FIFO by first insertion.
 * Intended for `<EquitiesErrorAlert />` only — every subscriber re-renders on any
 * error from any source. Feature components should not subscribe to this hook.
 */
export const [useEquitiesErrors] = bind(equitiesError$, {})
